import { useState, useEffect, useRef, useCallback } from "react";

import { v4 as uuidv4 } from "uuid";

type UseVersionCheckOptions = {
  intervalTime?: number;
  disabled?: boolean;
};

export default function useVersionCheck({
  intervalTime = 60000,
  disabled = false
}: UseVersionCheckOptions = {}) {
  const [ currentVersion, setCurrentVersion ] = useState<string | null>(null);
  const reloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasScheduledReload = useRef(false);

  const getVersionFromS3 = useCallback(async() => {
    try {
      const res = await fetch(
        `${window.location.origin}/version.json?v=${uuidv4()}`
      );
      if (!res.ok) {
        console.warn(
          "useVersionCheck: Failed to fetch version.json",
          res.status
        );
        return;
      }
      const content = await res.json();
      const version = content?.version;
      if (version) setCurrentVersion(version);
    }
    catch (err) {
      console.error("useVersionCheck-getVersionFromS3", err);
    }
  }, []);

  // Check version and schedule reload if needed
  useEffect(() => {
    if (disabled || !currentVersion) return;

    const localVersion = __BUILD_VERSION__;
    const versionsExistAndDifferent =
      !!currentVersion && !!localVersion && currentVersion !== localVersion;

    if (versionsExistAndDifferent && !hasScheduledReload.current) {
      hasScheduledReload.current = true;
      // Added this setTimeout to prevent service worker conflicts while updating the app
      reloadTimeoutRef.current = setTimeout(() => {
        window.location.reload();
      }, 10000);
    }

    return () => {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
    };
  }, [ disabled, currentVersion ]);

  // Polling and visibility change handling
  useEffect(() => {
    if (disabled) return;

    getVersionFromS3();

    const intervalId = setInterval(getVersionFromS3, intervalTime);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        getVersionFromS3();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [ disabled, intervalTime, getVersionFromS3 ]);

  return { currentVersion };
}
